import strings from 'strings'
import { formatCurrency, getHealthStatusLabel, getStatusStyle } from '../BalanceSheet/balanceSheetAdapters'
import { NOT_REPORTED, isReported, formatOptionalCurrency, formatDayLabel, formatPeriodRange } from '../IncomeStatement/incomeStatementAdapters'

export { formatCurrency, getHealthStatusLabel, getStatusStyle, NOT_REPORTED, isReported, formatOptionalCurrency, formatDayLabel, formatPeriodRange }

const figure = (raw) => ({
  reported: isReported(raw?.reported) ? Number(raw.reported) : null,
  derived: isReported(raw?.derived) ? Number(raw.derived) : null,
  value: isReported(raw?.value) ? Number(raw.value) : null,
  sourceLabel: raw?.sourceLabel || '',
  note: raw?.note || '',
  page: raw?.page || null
})

const lines = (items) => (Array.isArray(items) ? items : []).map((item, index) => ({
  key: `${item.rawLabel || 'line'}-${index}`,
  rawLabel: item.rawLabel || '',
  normalizedKey: item.normalizedKey || null,
  value: isReported(item.value) ? Number(item.value) : null,
  rawValue: isReported(item.rawValue) ? Number(item.rawValue) : null,
  page: item.page || null,
  parentLabel: item.parentLabel || ''
}))

const section = (raw) => ({
  lineItems: lines(raw?.lineItems),
  additionalLineItems: lines(raw?.additionalLineItems),
  byConcept: raw?.byConcept || {},
  netCash: figure(raw?.netCash)
})

// One period of the response, shaped for the cards: sections, reconciliation, disclosures and checks.
export const readPeriod = (raw) => ({
  periodId: raw?.periodId || '',
  label: raw?.label || raw?.periodId || '',
  startDate: raw?.startDate || '',
  endDate: raw?.endDate || '',
  periodType: raw?.periodType || 'UNKNOWN',
  basis: raw?.basis || 'UNKNOWN',
  durationMonths: isReported(raw?.durationMonths) ? Number(raw.durationMonths) : null,
  latest: raw?.latest === true,
  restated: raw?.restated === true,
  confidence: raw?.confidence || null,
  sections: {
    OPERATING: section(raw?.sections?.OPERATING),
    INVESTING: section(raw?.sections?.INVESTING),
    FINANCING: section(raw?.sections?.FINANCING)
  },
  reconciliation: {
    netChangeBeforeFx: figure(raw?.cashReconciliation?.netChangeBeforeFx),
    effectOfExchangeRates: figure(raw?.cashReconciliation?.effectOfExchangeRates),
    netChangeInCash: figure(raw?.cashReconciliation?.netChangeInCash),
    beginningCash: figure(raw?.cashReconciliation?.beginningCash),
    endingCash: figure(raw?.cashReconciliation?.endingCash),
    scope: raw?.cashReconciliation?.cashBalanceScope || 'UNKNOWN'
  },
  supplementalDisclosures: lines(raw?.supplementalDisclosures),
  noncashActivities: lines(raw?.noncashActivities),
  otherLineItems: lines(raw?.otherLineItems),
  checks: (Array.isArray(raw?.checks) ? raw.checks : []).map((check, index) => ({
    key: check.id || `check-${index}`,
    label: check.label || check.formula || '',
    status: check.status || 'NOT_APPLICABLE',
    verified: check.verified === true,
    note: check.note || '',
    expected: isReported(check.expected) ? Number(check.expected) : null,
    actual: isReported(check.actual) ? Number(check.actual) : null
  })),
  validationIssues: (Array.isArray(raw?.validationIssues) ? raw.validationIssues : []).map((issue) => ({
    rule: issue.rule || '',
    severity: issue.severity || 'INFO',
    message: issue.message || ''
  })),
  warnings: raw?.warnings || [],
  extractionDoubts: raw?.extractionDoubts || []
})

// The whole response: metadata, every period in printed order and the one selected as the latest.
export const readStatement = (doc) => {
  const periods = (Array.isArray(doc?.periods) ? doc.periods : []).map(readPeriod)
  const latestPeriodId = doc?.latestPeriodId || (periods.find((p) => p.latest) || {}).periodId || null
  return {
    valid: doc?.validCashFlowStatement !== false,
    documentType: doc?.documentType || null,
    error: doc?.error || null,
    entityName: doc?.entityName || '',
    consolidated: doc?.consolidated === true,
    accountingStandard: doc?.accountingStandard || 'UNKNOWN',
    operatingMethod: doc?.operatingMethod || 'UNKNOWN',
    currency: doc?.currency || '',
    scale: isReported(doc?.scale) ? Number(doc.scale) : 1,
    auditStatus: doc?.auditStatus || '',
    documentNotes: doc?.documentNotes || [],
    periods,
    // Columns the statement printed; only the main one (the last full year) is returned
    periodsOnStatement: isReported(doc?.periodsOnStatement) ? Number(doc.periodsOnStatement) : periods.length,
    latestPeriodId,
    latestPeriodAmbiguous: doc?.latestPeriodAmbiguous === true,
    unmappedLineItems: lines(doc?.unmappedLineItems),
    warnings: doc?.warnings || [],
    confidence: doc?.confidence || null,
    insights: readInsights(doc?.insights)
  }
}

const num = (value) => (isReported(value) ? Number(value) : null)

// The health score and the facts Document Insights computed from the verified figures of the main period
export const readInsights = (raw) => ({
  netIncome: num(raw?.netIncome),
  operatingCashFlow: num(raw?.operatingCashFlow),
  capitalExpenditures: num(raw?.capitalExpenditures),
  freeCashFlow: num(raw?.freeCashFlow),
  cashConversion: num(raw?.cashConversion),
  debtRepayments: num(raw?.debtRepayments),
  debtRepaymentCoverage: num(raw?.debtRepaymentCoverage),
  distributions: num(raw?.distributions),
  netChangeInCash: num(raw?.netChangeInCash),
  beginningCash: num(raw?.beginningCash),
  endingCash: num(raw?.endingCash),
  healthScore: isReported(raw?.health) ? Number(raw.health) : null,
  healthVerdictText: raw?.healthVeredict || '',
  healthDrivers: raw?.healthDrivers || [],
  strengths: raw?.strengths || [],
  observations: raw?.observations || [],
  concerns: raw?.concerns || [],
  takeaways: raw?.takeaways || []
})

// Headline of the banner, read off the same verdict as the score and refined by the figures within the tier
export const pickHeadline = (insights) => {
  if (insights.operatingCashFlow === null) return strings.CASH_FLOW_HEADLINE_INCOMPLETE
  if (insights.operatingCashFlow <= 0) return strings.CASH_FLOW_HEADLINE_BURN
  if (insights.freeCashFlow !== null && insights.freeCashFlow < 0) return strings.CASH_FLOW_HEADLINE_CAPEX
  if (insights.netChangeInCash !== null && insights.netChangeInCash < 0) return strings.CASH_FLOW_HEADLINE_DRAWDOWN
  const tier = getHealthStatusLabel(insights.healthScore || 0)
  if (tier === 'HEALTHY') return strings.CASH_FLOW_HEADLINE_SOLID
  return tier === 'WATCH' ? strings.CASH_FLOW_HEADLINE_WATCH : strings.CASH_FLOW_HEADLINE_AT_RISK
}

export const deriveSummary = (insights) => {
  const statusLabel = insights.healthScore === null ? 'AT_RISK' : getHealthStatusLabel(insights.healthScore)
  const takeaways = insights.takeaways || []
  return {
    statusLabel,
    statusText: insights.healthVerdictText || '',
    headline: pickHeadline(insights),
    narrative: takeaways[0] || '',
    bullets: takeaways.slice(1, 4)
  }
}

const pct = (value) => `${Number(value).toFixed(1)}%`

// Four tiles: operating cash flow, free cash flow, net change in cash and ending cash
export const deriveKeyIndicators = (insights) => [
  {
    key: 'operating',
    name: strings.CASH_FLOW_KPI_OPERATING,
    value: formatOptionalCurrency(insights.operatingCashFlow),
    sub: insights.cashConversion !== null
      ? `${pct(insights.cashConversion)} ${strings.CASH_FLOW_KPI_OF_NET_INCOME} ${formatOptionalCurrency(insights.netIncome)}`
      : strings.CASH_FLOW_KPI_NO_NET_INCOME,
    negative: insights.operatingCashFlow !== null && insights.operatingCashFlow < 0
  },
  {
    key: 'freeCashFlow',
    name: strings.CASH_FLOW_KPI_FREE_CASH_FLOW,
    value: formatOptionalCurrency(insights.freeCashFlow),
    sub: insights.capitalExpenditures !== null
      ? `${strings.CASH_FLOW_KPI_AFTER_CAPEX} ${formatOptionalCurrency(Math.abs(insights.capitalExpenditures))}`
      : strings.CASH_FLOW_KPI_NO_CAPEX,
    negative: insights.freeCashFlow !== null && insights.freeCashFlow < 0
  },
  {
    key: 'netChange',
    name: strings.CASH_FLOW_KPI_NET_CHANGE,
    value: formatOptionalCurrency(insights.netChangeInCash),
    sub: insights.debtRepaymentCoverage !== null
      ? `${strings.CASH_FLOW_KPI_DEBT_COVERAGE} ${insights.debtRepaymentCoverage.toFixed(2)}x (${formatOptionalCurrency(insights.debtRepayments)} ${strings.CASH_FLOW_KPI_REPAID})`
      : strings.CASH_FLOW_KPI_NO_DEBT,
    negative: insights.netChangeInCash !== null && insights.netChangeInCash < 0
  },
  {
    key: 'endingCash',
    name: strings.CASH_FLOW_KPI_ENDING_CASH,
    value: formatOptionalCurrency(insights.endingCash),
    sub: insights.beginningCash !== null ? `${strings.CASH_FLOW_KPI_FROM} ${formatOptionalCurrency(insights.beginningCash)}` : strings.CASH_FLOW_KPI_NO_BEGINNING,
    negative: false
  }
]

// Only what code established, concerns first, then observations, then strengths
export const deriveFlags = (insights) => [
  ...insights.concerns.map((text) => ({ severity: 'Concern', label: strings.INCOME_STATEMENT_SEVERITY_CONCERN, line: text })),
  ...insights.observations.map((text) => ({ severity: 'Observation', label: strings.INCOME_STATEMENT_SEVERITY_OBSERVATION, line: text })),
  ...insights.strengths.map((text) => ({ severity: 'Positive', label: strings.INCOME_STATEMENT_SEVERITY_POSITIVE, line: text }))
]

// Every line of the main period in one table, each with its section, as the income statement's cost structure
export const deriveStructure = (period) => {
  const rows = []
  const push = (items, type, extra = {}) => items.forEach((item) => rows.push({ ...item, type, ...extra }))
  push(period.sections.OPERATING.lineItems, strings.CASH_FLOW_TYPE_OPERATING)
  push(period.sections.INVESTING.lineItems, strings.CASH_FLOW_TYPE_INVESTING)
  push(period.sections.FINANCING.lineItems, strings.CASH_FLOW_TYPE_FINANCING)
  const totals = [
    { key: 'netOperating', label: strings.CASH_FLOW_NET_CASH_OPERATING, figure: period.sections.OPERATING.netCash },
    { key: 'netInvesting', label: strings.CASH_FLOW_NET_CASH_INVESTING, figure: period.sections.INVESTING.netCash },
    { key: 'netFinancing', label: strings.CASH_FLOW_NET_CASH_FINANCING, figure: period.sections.FINANCING.netCash },
    { key: 'netChange', label: strings.CASH_FLOW_NET_CHANGE, figure: period.reconciliation.netChangeInCash }
  ]
  return { rows, totals }
}

export const methodText = (method) => {
  if (method === 'DIRECT') return strings.CASH_FLOW_METHOD_DIRECT
  if (method === 'INDIRECT') return strings.CASH_FLOW_METHOD_INDIRECT
  return strings.CASH_FLOW_METHOD_UNKNOWN
}

export const scaleText = (scale) => {
  if (!scale || scale === 1) return strings.CASH_FLOW_SCALE_UNITS
  if (scale === 1000) return `${strings.CASH_FLOW_SCALE_PREFIX} thousands`
  if (scale === 1000000) return `${strings.CASH_FLOW_SCALE_PREFIX} millions`
  return `${strings.CASH_FLOW_SCALE_PREFIX} ×${scale}`
}

export const scopeText = (scope) => {
  if (scope === 'CASH') return 'cash'
  if (scope === 'CASH_AND_CASH_EQUIVALENTS') return 'cash and cash equivalents'
  if (scope === 'CASH_CASH_EQUIVALENTS_AND_RESTRICTED_CASH') return 'cash, cash equivalents and restricted cash'
  return ''
}

// A concept key as a readable label: "change_in_accounts_receivable" -> "Change in accounts receivable".
export const conceptText = (key) => {
  if (!key) return ''
  const text = key.replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}
